import { useState, useEffect, useRef, useCallback } from "react";
import { ProjectMedia, PresignedUrlResponse } from "@/types";
import { fetchMediaPresignedUrl } from "@/services/api";
import { useIsMobile } from "@/hooks/use-mobile";
import { Spinner } from "@/components/ui/spinner";
import { ImageOff, PlayIcon } from "lucide-react";

interface ProjectMediaThumbnailGalleryProps {
  projectId: string;
  media: ProjectMedia[];
  onImageClick: (index: number) => void;
}

const ProjectMediaThumbnailGallery = ({
  projectId,
  media,
  onImageClick,
}: ProjectMediaThumbnailGalleryProps) => {
  const [presignedUrls, setPresignedUrls] = useState<
    Record<string, PresignedUrlResponse>
  >({});
  const [loadingUris, setLoadingUris] = useState<Record<string, boolean>>({});
  const presignedUrlsRef = useRef<Record<string, PresignedUrlResponse>>({});
  const loadingUrisRef = useRef<Record<string, boolean>>({});
  const observers = useRef<Record<string, IntersectionObserver>>({});
  const isMobile = useIsMobile();

  useEffect(() => {
    presignedUrlsRef.current = presignedUrls;
  }, [presignedUrls]);

  useEffect(() => {
    loadingUrisRef.current = loadingUris;
  }, [loadingUris]);

  const fetchPresignedUrl = useCallback(async (uri: string) => {
    if (presignedUrlsRef.current[uri] || loadingUrisRef.current[uri]) return;

    setLoadingUris((prev) => ({ ...prev, [uri]: true }));

    try {
      const result = await fetchMediaPresignedUrl(projectId, uri);
      if (result) {
        setPresignedUrls((prev) => ({ ...prev, [uri]: result }));
      }
    } catch (error) {
      console.error(`Error loading presigned URL for ${uri}:`, error);
    } finally {
      setLoadingUris((prev) => ({ ...prev, [uri]: false }));
    }
  }, [projectId]);

  // Set up intersection observer to track visible items
  useEffect(() => {
    // Clean up previous observers
    Object.values(observers.current).forEach((observer) =>
      observer.disconnect(),
    );
    observers.current = {};

    // Create new observers for each item
    media.forEach((item) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              fetchPresignedUrl(item.uri);
            }
          });
        },
        { threshold: 0.1, rootMargin: "100px" }, // Load images when they're close to viewport
      );

      observers.current[item.uri] = observer;
    });

    return () => {
      // Clean up on unmount
      Object.values(observers.current).forEach((observer) =>
        observer.disconnect(),
      );
      observers.current = {};
    };
  }, [media, fetchPresignedUrl]);

  // Prefetch just the first image on mount regardless of visibility
  useEffect(() => {
    if (media.length > 0) {
      fetchPresignedUrl(media[0].uri);
    }
  }, [media, fetchPresignedUrl]);

  // Add a helper function to determine if the media is a video
  const isVideo = (fileName: string) => {
    const videoExtensions = ["mp4", "webm", "ogg"];
    const extension = fileName.split(".").pop()?.toLowerCase();
    return extension ? videoExtensions.includes(extension) : false;
  };

  if (!media.length) {
    return null;
  }

  return (
    <div className="w-full mt-4">
      <div
        className={`grid gap-4 ${
          isMobile
            ? "grid-cols-2"
            : "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6"
        }`}
      >
        {media.map((item, index) => (
          <div
            key={item.uri}
            className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-primary transition-all cursor-pointer"
            ref={(el) => {
              if (el && observers.current[item.uri]) {
                observers.current[item.uri].observe(el);
              }
            }}
            onClick={() => {
              fetchPresignedUrl(item.uri);
              onImageClick(index);
            }}
            onMouseEnter={() => fetchPresignedUrl(item.uri)}
          >
            {presignedUrls[item.uri] ? (
              isVideo(item.fileName) ? (
                <>
                  <video
                    src={presignedUrls[item.uri]?.downloadUrl}
                    controls={false}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      className="bg-black bg-opacity-50 text-white rounded-full p-2"
                      onClick={() => onImageClick(index)}
                    >
                      <PlayIcon className="h-6 w-6" />
                    </button>
                  </div>
                </>
              ) : (
                <img
                  src={presignedUrls[item.uri].thumbnailDownloadUrl}
                  alt={item.fileName}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )
            ) : loadingUris[item.uri] ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <Spinner className="h-6 w-6" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <ImageOff className="h-6 w-6 text-gray-400" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectMediaThumbnailGallery;
